import { config } from 'config/env';

/**
 * Handoff para a plataforma de IA.
 *
 * O front novo roda em outra origem, então não alcança o localStorage daqui —
 * política de mesma origem. Quem entrega o JWT é esta página, e ele vai no CORPO
 * de um form POST: é o binding que o SAML chama de HTTP-POST e o OIDC de
 * `form_post`. Lá o token é trocado por um cookie de sessão e descartado.
 *
 * Não vai na query string de propósito. Credencial em URL sobra em três lugares
 * que não controlamos: o histórico do browser, o access log do ingress e o
 * cabeçalho `Referer`. O `/sso` do outro lado só aceita POST — um GET com
 * `?token=` responde 405.
 */

const CHAVE_DO_AUTH = 'persist:v713-demo1-auth';

/**
 * O destino precisa ser TLS. O POST tira o token da URL, mas não o protege em
 * trânsito: em `http://` o corpo da requisição viaja legível para qualquer
 * intermediário, igual à query string. `z.url()` sozinho aceita `http://`, por
 * isso a checagem mora aqui e não só no schema da env.
 *
 * localhost fica de fora da exigência porque o browser já o trata como origem
 * segura e é o que o desenvolvedor usa para subir o front novo.
 */
function isOrigemSegura(url: string): boolean {
  try {
    const { protocol, hostname } = new URL(url);
    if (protocol === 'https:') return true;
    return (
      protocol === 'http:' &&
      (hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname === '[::1]')
    );
  } catch (_) {
    return false;
  }
}

/**
 * O destino do handoff está utilizável?
 *
 * Sem URL o desvio fica desligado mesmo para quem o backend liberou: é a válvula
 * de emergência sem deploy de código. URL insegura desliga do mesmo jeito — o
 * advogado cai no JusGPT, que funciona, em vez de vazar o token.
 *
 * A leitura é opcional de propósito: config incompleto (um mock parcial num
 * spec, por exemplo) não pode derrubar o menu inteiro — desliga o desvio.
 */
function destinoUtilizavel(): boolean {
  const url = config.aiPlatform?.url;
  if (!url) return false;
  return isOrigemSegura(url);
}

/**
 * O JWT do advogado, como o redux-persist o deixou. As aspas vêm de o persist
 * serializar cada campo do slice por conta própria.
 */
export function getJusfyAuthToken(): string {
  try {
    const persisted = localStorage.getItem(CHAVE_DO_AUTH);
    if (!persisted) return '';
    const parsed = JSON.parse(persisted) as { authToken?: string };
    return (parsed.authToken || '').replace(/"/g, '');
  } catch (_) {
    return '';
  }
}

/** O endpoint do handoff. Sem token: ele vai no corpo, não aqui. */
export function aiPlatformSsoUrl(): string {
  const base = (config.aiPlatform?.url || '').replace(/\/+$/, '');
  return `${base}/sso`;
}

/**
 * Abre a plataforma de IA em aba nova. Devolve `false` quando não há token ou
 * quando o destino não está utilizável — aí quem chamou segue com a navegação
 * normal pro JusGPT, que funciona. Nunca mostramos erro: o advogado não tem o
 * que fazer com essa informação.
 *
 * Um form submetido e descartado, não `window.open`: é assim que o JWT vai no
 * corpo em vez da URL. O form precisa estar no documento para o browser aceitar
 * o `submit()`, e sai no mesmo tick — a submissão já foi despachada, então o
 * token não fica pendurado no DOM.
 *
 * `<form>` não aceita `rel="noopener"`, então o isolamento da aba nova vem do
 * outro lado: o `/sso` responde `Cross-Origin-Opener-Policy: same-origin`.
 *
 * Popup blocker não interfere: a submissão nasce de um clique do advogado.
 */
export function openAiPlatform(): boolean {
  if (!destinoUtilizavel()) return false;

  const token = getJusfyAuthToken();
  if (!token) return false;

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = aiPlatformSsoUrl();
  form.target = '_blank';
  form.style.display = 'none';

  const campo = document.createElement('input');
  campo.type = 'hidden';
  campo.name = 'token';
  campo.value = token;
  form.appendChild(campo);

  document.body.appendChild(form);
  try {
    form.submit();
  } finally {
    form.remove();
  }
  return true;
}
